/** @file Activity feed pages over GET /changes (summaries only): the latest 50, then 50 older at a time. */
import { docStore } from '../../stores/docStore';
import { liveStore } from '../../stores/liveStore';
import { liveSession, type SyncSession } from './liveSession';
import { mergeByKey } from './mergeByKey';
import { pushNotice } from './pushNotice';

const PAGE = 50;
const MAX_FEED = 5000;

/** Reads the summaries in (sinceRev, sinceRev + limit] into the feed; a failure becomes a notice. */
async function read(session: SyncSession, sinceRev: number, limit: number): Promise<void> {
  try {
    const page = await session.client.getChanges(session.projectId, sinceRev, { patches: false, limit });
    if (liveSession.get() === session) liveStore.setState((s) => ({ commits: mergeByKey(s.commits, page.changes, (c) => c.rev, MAX_FEED) }));
  } catch (thrown) {
    pushNotice(null, thrown);
  }
}

/** Seeding at project open (and after each resync) and paging back from the oldest summary shown. */
export const feedPages = {
  /** The last 50 commits up to the loaded rev. */
  seed(session: SyncSession): Promise<void> {
    const rev = docStore.getState().slice?.rev ?? 0;
    return read(session, Math.max(0, rev - PAGE), PAGE);
  },
  /** The summary of `rev` unless the feed holds it (a rev a resync reached: GET /doc writes no feed, fx1 T-463). */
  holding(session: SyncSession, rev: number): Promise<void> {
    return liveStore.getState().commits.some((c) => c.rev === rev) ? Promise.resolve() : read(session, rev - 1, 1);
  },
  /** The 50 commits before the oldest one in the feed (nothing below rev 1). */
  older(): Promise<void> {
    const session = liveSession.get();
    const oldest = liveStore.getState().commits[0]?.rev ?? (docStore.getState().slice?.rev ?? 0) + 1;
    if (session === null || oldest <= 1) return Promise.resolve();
    const since = Math.max(0, oldest - 1 - PAGE);
    return read(session, since, oldest - 1 - since);
  },
};
